import {gql} from '@apollo/client';

import {
  IsAntibodyQuery,
  IsAntibodyQuery_antibody_AntibodyNode as IsAntibodyQueryAntibodyNode,
  IsAntibodyQueryVariables,
} from 'antibodies/__generated/IsAntibodyQuery';
import {updateAntibodyUIOpenVersionId} from 'antibodies/hooks/useAntibodyUIState';
import BenchlingConfig from 'BenchlingConfig';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import APIUtil from 'util/APIUtil';

export interface AntibodyStateData {
  isAntibody: boolean;
}

const IS_ANTIBODY_QUERY = gql`
  query IsAntibodyQuery($antibodyId: StrictString!) @skipAudit @engComponent(name: ANTIBODIES) {
    antibody(antibodyId: $antibodyId) {
      ... on AntibodyNode {
        id
        currentVersionId
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Queries for initial antibody data.
 * Exported for testing.
 * Short-circuits if antibodies aren't enabled, or if the file is not a custom entity / BasicFolderItem.
 * @param fileId The API ID of the file to check
 */
export async function fetchAntibodyData(fileId: string): Promise<IsAntibodyQueryAntibodyNode | null> {
  if (!BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENTITIES || !APIUtil.isBasicFolderItem(fileId)) {
    return null;
  }

  const {antibody} = await doQuerySafe<IsAntibodyQuery, IsAntibodyQueryVariables>(
    {query: IS_ANTIBODY_QUERY},
    {variables: {antibodyId: fileId}}
  );

  // This is the expected result if the file is truly inaccessible, but it is also the expected result if the
  // item is not an antibody.
  if (isInaccessible(antibody)) {
    return null;
  }

  return antibody;
}

/**
 * Queries for whether a file is an antibody and initializes the antibody state data.
 * @param fileId The API ID of the file to check
 */
export async function initializeAntibodyDataOnOpen(fileId: string): Promise<AntibodyStateData | null> {
  const antibody = await fetchAntibodyData(fileId);
  if (antibody == null) {
    return null;
  }

  updateAntibodyUIOpenVersionId(fileId, antibody.currentVersionId);
  return {isAntibody: true};
}

/**
 * Queries for whether a file is an antibody.
 * @param fileId The API ID of the file to check
 */
export async function getIsAntibody(fileId: string): Promise<boolean> {
  const antibody = await fetchAntibodyData(fileId);
  return antibody != null;
}
